import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import Icon from './Icon';
import { useModal } from '../context/ModalContext';
import '../styles/ActivityLogPanel.css';
import CustomSelect from './CustomSelect';

const ROLE_LABELS = {
  operator: 'Operator',
  client: 'Client',
  admin: 'Admin',
};

const ROLE_COLORS = {
  operator: '#3B82F6',
  client: '#10B981',
  admin: '#F59E0B',
};

const ENTITY_LABELS = {
  idea: 'Idea',
  content_approval: 'Content',
  content_draft: 'Draft',
  user: 'User Management',
  brand_voice: 'Brand Voice',
};

function formatDate(dateStr) {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-US', {
    month: 'numeric',
    day: 'numeric',
    year: 'numeric'
  });
}

function formatFullDate(dateStr) {
  if (!dateStr) return '';
  return new Date(dateStr).toLocaleString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
    hour: 'numeric', minute: '2-digit', hour12: true
  });
}

const ActivityLogPanel = ({ user }) => {
  const modal = useModal();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [noAgency, setNoAgency] = useState(false);
  const [roleFilter, setRoleFilter] = useState('');
  const [entityTypeFilter, setEntityTypeFilter] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [offset, setOffset] = useState(0);
  const [loadingMore, setLoadingMore] = useState(false);
  const [selectedLogs, setSelectedLogs] = useState([]);
  const [deleting, setDeleting] = useState(false);

  const LIMIT = 50;

  const fetchLogs = useCallback(async (currentOffset = 0, append = false) => {
    if (append) {
      setLoadingMore(true);
    } else {
      setLoading(true);
    }

    try {
      const params = { limit: LIMIT, offset: currentOffset };
      if (roleFilter) params.role_filter = roleFilter;
      if (entityTypeFilter) params.entity_type = entityTypeFilter;
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;

      const response = await axios.get('/api/admin/activity-log', {
        params,
        withCredentials: true
      });

      const data = response.data;

      if (data.no_agency) {
        setNoAgency(true);
        setLogs([]);
        setTotal(0);
        return;
      }

      setNoAgency(false);
      setTotal(data.total || 0);

      if (append) {
        setLogs(prev => [...prev, ...(data.logs || [])]);
      } else {
        setLogs(data.logs || []);
      }
    } catch (error) {
      console.error('Error fetching activity log:', error);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [roleFilter, entityTypeFilter, dateFrom, dateTo]);

  // Re-fetch when filters change (reset pagination)
  useEffect(() => {
    setOffset(0);
    fetchLogs(0, false);
  }, [fetchLogs]);

  const handleLoadMore = () => {
    const newOffset = offset + LIMIT;
    setOffset(newOffset);
    fetchLogs(newOffset, true);
  };

  const toggleLogSelect = (logId) => {
    setSelectedLogs(prev => {
      if (prev.includes(logId)) {
        return prev.filter(id => id !== logId);
      } else {
        return [...prev, logId];
      }
    });
  };

  const selectAllLogs = () => {
    const allLogIds = logs.map(log => log._id);
    const allSelected = allLogIds.length > 0 && allLogIds.every(id => selectedLogs.includes(id));

    if (allSelected) {
      setSelectedLogs([]);
    } else {
      setSelectedLogs(allLogIds);
    }
  };

  const handleBatchDelete = async () => {
    if (selectedLogs.length === 0) return;

    const confirmMessage = `Are you sure you want to delete ${selectedLogs.length} activity log ${selectedLogs.length === 1 ? 'entry' : 'entries'}?`;
    const result = await modal.confirm(confirmMessage, 'Delete Activity Logs');
    if (!result) {
      return;
    }

    try {
      setDeleting(true);

      // Delete all selected logs
      await Promise.all(
        selectedLogs.map(logId =>
          axios.delete(`/api/admin/activity-log/${logId}`, {
            withCredentials: true
          })
        )
      );

      // Remove deleted logs from the list
      setLogs(prev => prev.filter(log => !selectedLogs.includes(log._id)));
      setTotal(prev => prev - selectedLogs.length);
      setSelectedLogs([]);
    } catch (error) {
      console.error('Error deleting activity logs:', error);
      modal.error('Failed to delete some activity log entries');
    } finally {
      setDeleting(false);
    }
  };

  const hasMore = logs.length < total;

  return (
    <div className="al-panel">
      <div className="al-header">
        <p className="al-subtitle">Track operator, client, and admin actions across your agency.</p>
      </div>

      <div className="al-filters">
        <div className="al-filter-group">
          <span className="al-filter-label">Role</span>
          <div className="al-role-pills">
            {[['', 'All'], ['operator', 'Operators'], ['client', 'Clients'], ['admin', 'Admins']].map(([val, label]) => (
              <button
                key={val}
                className={`al-role-pill${roleFilter === val ? ' active' : ''}`}
                style={roleFilter === val && val !== '' ? { backgroundColor: ROLE_COLORS[val], borderColor: ROLE_COLORS[val] } : {}}
                onClick={() => setRoleFilter(val)}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        <div className="al-filter-group">
          <span className="al-filter-label">Type</span>
          <CustomSelect
              className="al-type-select"
              value={entityTypeFilter}
              onChange={e => setEntityTypeFilter(e.target.value)}
            >
              <option value="">All Types</option>
              <option value="idea">Ideas</option>
              <option value="content_approval">Content Approval</option>
              <option value="content_draft">Content Drafts</option>
            </CustomSelect>
        </div>

        <div className="al-filter-group">
          <span className="al-filter-label">From</span>
          <input
            type="date"
            className="al-date-input"
            value={dateFrom}
            onChange={e => setDateFrom(e.target.value)}
          />
        </div>

        <div className="al-filter-group">
          <span className="al-filter-label">To</span>
          <input
            type="date"
            className="al-date-input"
            value={dateTo}
            onChange={e => setDateTo(e.target.value)}
          />
        </div>
      </div>

      {loading ? (
        <div className="al-loading">
          <div className="al-spinner"></div>
          <span>Loading activity...</span>
        </div>
      ) : noAgency ? (
        <div className="al-empty">
          <Icon name="building" size="2rem" />
          <p>Select an agency to view activity.</p>
        </div>
      ) : logs.length === 0 ? (
        <div className="al-empty">
          <Icon name="feeds" size="2rem" />
          <p>No activity found for the selected filters.</p>
        </div>
      ) : (
        <>
          <div className="al-action-bar">
            <div className="al-select-all">
              <input
                type="checkbox"
                className="al-checkbox"
                checked={logs.length > 0 && logs.every(log => selectedLogs.includes(log._id))}
                onChange={selectAllLogs}
              />
              <span className="al-select-label">Select All</span>
            </div>
            <div className="al-count-text">
              Showing {logs.length} of {total} {total === 1 ? 'entry' : 'entries'}
            </div>
            <button
              className={`btn-batch-discard${selectedLogs.length === 0 ? ' btn-batch-inactive' : ''}`}
              onClick={handleBatchDelete}
              disabled={selectedLogs.length === 0 || deleting}
            >
              {deleting ? 'Deleting...' : `Discard${selectedLogs.length > 0 ? ` ${selectedLogs.length}` : ''}`}
            </button>
          </div>
          <div className="al-feed">
            {logs.map((log) => {
              const role = log.performed_by_role || '';
              const roleColor = ROLE_COLORS[role] || '#9CA3AF';
              const initial = (log.performed_by_name || '?').charAt(0).toUpperCase();
              const entityLabel = ENTITY_LABELS[log.entity_type] || log.entity_type;
              const isSelected = selectedLogs.includes(log._id);

              return (
                <div key={log._id} className={`al-entry${isSelected ? ' al-entry-selected' : ''}`}>
                  <input
                    type="checkbox"
                    className="al-checkbox"
                    checked={isSelected}
                    onChange={() => toggleLogSelect(log._id)}
                  />
                  <div
                    className="al-avatar"
                    style={{ backgroundColor: roleColor }}
                    title={log.performed_by_name || ''}
                  >
                    {initial}
                  </div>
                  <div className="al-entry-body">
                    <div className="al-entry-main">
                      <span className="al-name">{log.performed_by_name || 'Unknown'}</span>
                      {role && (
                        <span
                          className="al-role-badge"
                          style={{ backgroundColor: roleColor }}
                        >
                          {ROLE_LABELS[role] || role}
                        </span>
                      )}
                      <span className="al-action-summary">
                        {log.action_summary || log.action || ''}
                      </span>
                    </div>
                    <div className="al-entry-meta">
                      {log.entity_type && (
                        <span className="al-entity-chip">{entityLabel}</span>
                      )}
                      {log.entity_title && log.entity_title !== 'Content Draft' && (
                        <span className="al-entity-title">{log.entity_title}</span>
                      )}
                      {log.organization_name && (
                        <span className="al-context-badge al-org-badge">
                          <Icon name="building" size="0.7rem" />
                          {log.organization_name}
                        </span>
                      )}
                      {log.brand_name && (
                        <span className="al-context-badge al-brand-badge">
                          <Icon name="tag" size="0.7rem" />
                          {log.brand_name}
                        </span>
                      )}
                      <span
                        className="al-time"
                        title={formatFullDate(log.created_at)}
                      >
                        {formatDate(log.created_at)}
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {hasMore && (
            <div className="al-load-more-wrapper">
              <button
                className="al-load-more"
                onClick={handleLoadMore}
                disabled={loadingMore}
              >
                {loadingMore ? 'Loading...' : `Load More (${total - logs.length} remaining)`}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default ActivityLogPanel;
